import type { Logger, LoggerFactory } from '@amp/web-apps-logger/src';
import {
    type InvalidationAction,
    type Page,
    type PageChangeInstruction,
    type PageInvalidationRules,
    type PageEventTrigger,
    type SectionChangeInstruction,
    type SectionInvalidationRules,
    type SectionEventTrigger,
    type ComponentModel,
    type Section,
    type PaginationResult,
    type ClassicalListItem,
    isMultipleChangePaginationResult,
    tracklistDescription,
} from '~/jet/models';
import { paginationStore } from '~/stores/pagination';
import { formatDuration } from '~/utils/datetime';

export type ChangeInstruction =
    | PageChangeInstruction
    | SectionChangeInstruction;

export type InvalidationRules =
    | PageInvalidationRules
    | SectionInvalidationRules;
type EventTrigger = PageEventTrigger | SectionEventTrigger;
type MetaData = {
    historyOperation?: 'replaceState' | 'pushState';
};
export type InvalidationIntent = EventTrigger['intent'] & {
    metadata?: MetaData;
};

type InvalidationEventDetails = {
    metadata?: InvalidationAction['metadata'];
} & (
    | {
          scope: InvalidationAction['scope'];
          kind: InvalidationAction['kind'];
      }
    | {
          name: string;
      }
);

type InvalidationCallback = (
    details: OnChangeDetails,
    afterPageUpdate?: AfterPageUpdate,
) => void;

type OnChangeDetails = {
    sectionId?: Section<unknown>['id'];
    intent: InvalidationIntent;
};
type AfterPageUpdate = (updatedPage: Page, details: OnChangeDetails) => Page;
type ChangeEventHandler = (
    details: OnChangeDetails,
    updatePage: (
        currentPage: Page,
        changeInstruction: ChangeInstruction,
    ) => Page,
) => void;

export class Invalidations {
    private logger: Logger;

    private onchangeListeners: ChangeEventHandler[] = [];

    private events: Map<
        string,
        {
            eventTrigger: EventTrigger;
            event: Event;
            callback: InvalidationCallback;
        }[]
    > = new Map();

    // Map with callback: timeoutId
    private delayedEvents: Map<() => void, ReturnType<typeof setTimeout>> =
        new Map();

    constructor(logger: LoggerFactory) {
        this.logger = logger.loggerFor('InvalidationHandler');
    }

    public monitorPage(page: Page) {
        this.events.clear();
        this.clearDelayedEvents();

        // Setup page invalidation rules
        // Library Pages can set custom invalidation rules with webInvalidationRules
        this.logger.info(
            'setup page invalidation rules:',
            page.invalidationRules,
        );
        'webInvalidationRules' in page &&
            this.setupInvalidationRules(page.webInvalidationRules, this.change);
        'invalidationRules' in page &&
            this.setupInvalidationRules(page.invalidationRules, this.change);

        // Setup sections invalidation rules
        page.sections?.forEach((section, index) => {
            if (section.invalidationRules) {
                this.logger.info(
                    'setup section invalidation rules:',
                    index,
                    section.invalidationRules,
                );

                this.setupInvalidationRules(
                    section.invalidationRules,
                    // Need to pass section id as well so the handler knows what section to update
                    (
                        details: OnChangeDetails,
                        afterPageUpdate: AfterPageUpdate,
                    ) => {
                        this.change(
                            {
                                ...details,
                                sectionId: section.id,
                            },
                            afterPageUpdate,
                        );
                    },
                );
            }
        });

        // Pagination Invalidations

        paginationStore.setHasNextPage(!!page.nextIntent);
        if (page.nextIntent) {
            const nextInvalidationRules: InvalidationRules = {
                eventTriggers: [
                    {
                        events: [{ name: 'nextPage' }],
                        intent: page.nextIntent,
                    },
                ],
            };
            this.logger.info('setup next page:', nextInvalidationRules);
            this.setupInvalidationRules(
                nextInvalidationRules,
                async (intent) => {
                    paginationStore.setLoadingNextPage(true);

                    try {
                        await this.change(intent);
                    } finally {
                        // reset loading spinner
                        paginationStore.setLoadingNextPage(false);
                    }
                },
            );
        }
    }

    /**
     *
     * @param invalidationRules { eventTriggers: ({ intent, events })[] }
     * @param handleIntent a function that will get called with the intent when the event got triggered
     */
    private setupInvalidationRules(
        invalidationRules: InvalidationRules,
        onChange: InvalidationCallback,
    ) {
        invalidationRules?.eventTriggers?.forEach(
            (eventTrigger: EventTrigger) => {
                // Do not save all events, but only the single one that is covered in this loop
                const trigger = { ...eventTrigger };
                delete trigger.events;

                // The same handler can be triggered by multiple different events
                eventTrigger.events.forEach((event) => {
                    // for expirationDate events, trigger directly with a delay
                    if (event.name === 'expirationDate') {
                        // Note: expirationDate events can have an endDate. Not sure what to do with that yet.
                        // TODO rdar://100020976 (JMOTW: Invalidation support follow-up: deferIntentUntilEvents + more documentation)

                        let delay =
                            event.date && Date.parse(event.date) - +new Date();

                        // Setting this up for pages that always need a reload when coming back to the page after a while.
                        // These only get triggered when loading the page again (for now), instead of after a set delay.
                        if ('deferIntentUntilEvents' in eventTrigger) {
                            const deferUntil =
                                eventTrigger.deferIntentUntilEvents;
                            const includesPageAppearanceUpdate =
                                deferUntil.includes('nextPageDisappearance') ||
                                deferUntil.includes('nextPageAppearance');
                            // Use a threshold of 2 seconds to avoid infinite loops when the expirationDate equals the time the page was loaded.
                            // 2 seconds because there might be some time between fetching the page and setting up the invalidations.
                            const hasPassedExpirationDate = delay < -2000;

                            // Only proceed (trigger defered expiration event) when it should defer until next page disappearance and it's not the initial load
                            if (
                                !includesPageAppearanceUpdate ||
                                !hasPassedExpirationDate
                            ) {
                                return;
                            }
                        }

                        // Set delay to 0 (trigger immediately) when it's in the past.
                        // It will get removed from the page for next loads.
                        delay = Math.max(delay, 0);

                        this.logger.info(
                            'setup expirationDate event',
                            formatDuration(delay / 1000, 'us'),
                            { details: trigger, event, delay },
                        );

                        this.delayEvent(() => {
                            this.logger.info('execute expirationDate event', {
                                details: trigger,
                                event,
                                delay,
                            });
                            onChange(
                                {
                                    intent: {
                                        ...eventTrigger.intent,
                                        ...('invalidatingItemIDs' in
                                        eventTrigger
                                            ? {
                                                  invalidatedItemIDs:
                                                      eventTrigger.invalidatingItemIDs,
                                              }
                                            : {}),
                                    },
                                },
                                (updatedPage, details) =>
                                    removeInvalidationRulesFromPage(
                                        updatedPage,
                                        { ...details, trigger, event },
                                    ),
                            );
                        }, delay);
                        return;
                    }

                    if ('deferIntentUntilEvents' in eventTrigger) {
                        // TODO rdar://100020976 (JMOTW: Invalidation support follow-up: tests, logging, more changeInstruction use cases)
                        // Skip these for now, as we don't know how to handle the deferIntentUntilEvents yet.
                        // Those with an expirationDate page appearance change are handled in the delayed section above.
                        // Only available on PageEventTrigger.
                        return;
                    }

                    let callback = onChange;

                    if (event.delay) {
                        callback = (...args) =>
                            this.delayEvent(
                                () => onChange(...args),
                                event.delay,
                            );
                    }

                    const eventName = getEventName(event);
                    if (eventName) {
                        // Allow more rules to listen to the same event
                        const eventsForThisName =
                            this.events.get(eventName) || [];
                        eventsForThisName.push({
                            eventTrigger: trigger,
                            event,
                            callback,
                        });
                        this.events.set(eventName, eventsForThisName);
                    }
                });
            },
        );
    }

    /**
     * Adds a listener for any page or section update
     * @param callback
     */
    public onChange = (callback: ChangeEventHandler) => {
        this.onchangeListeners.push(callback);
    };

    private change = async (
        details: OnChangeDetails,
        afterPageUpdate?: AfterPageUpdate,
    ): Promise<void> => {
        const afterUpdate = afterPageUpdate
            ? (currentPage: Page) => {
                  return afterPageUpdate(currentPage, details);
              }
            : null;

        const updatePage = (
            currentPage: Page,
            changeInstruction: ChangeInstruction,
        ) => {
            return updatePageFromChangeInstruction(
                currentPage,
                changeInstruction,
                details,
                afterUpdate,
            );
        };

        const allChangedListeners = this.onchangeListeners.map((listener) =>
            listener(details, updatePage),
        );
        await Promise.all(allChangedListeners);
    };

    public trigger(details: InvalidationEventDetails) {
        const eventName = getEventName(details);
        const events = this.events.get(eventName);

        this.logger.info('received invalidation trigger:', eventName, {
            trigger: details,
            registeredEvents: this.events,
            events,
        });

        if (!events) {
            // no active listeners for this event
            return;
        }

        events.forEach(({ eventTrigger, callback }) => {
            try {
                const detailsMeta = details.metadata;
                const intentMeta =
                    'metadata' in eventTrigger.intent &&
                    eventTrigger.intent.metadata;
                const metadata =
                    detailsMeta && intentMeta
                        ? { ...detailsMeta, ...intentMeta }
                        : intentMeta
                        ? intentMeta
                        : detailsMeta;
                const intent = {
                    ...eventTrigger.intent,
                    metadata,
                };
                callback({ intent });
            } catch (e) {
                this.logger.error(
                    'trigger invalidation event error:',
                    { details, eventTrigger },
                    e,
                );
            }
        });
    }

    private delayEvent(callback: () => void, delay: number) {
        const timeoutId = setTimeout(() => {
            callback();
            this.delayedEvents.delete(callback);
        }, delay);
        this.delayedEvents.set(callback, timeoutId);
    }

    private clearDelayedEvents() {
        for (const timeoutId of this.delayedEvents.values()) {
            clearTimeout(timeoutId);
        }
        this.delayedEvents.clear();
    }
}

/**
 *
 * @param event InvalidationAction trigger | InvalidationRules EventTrigger
 * @returns eventName: string
 */
function getEventName(event: {
    kind?: string;
    name?: string;
    scope?: string;
}): string {
    // InvalidationActions have kind, eventTriggers have either kind or name.
    const name = event.kind || event.name;
    // Only InvalidationActions have a scope, not custom created events.
    // If it doesn't have a scope, use our inapp scope.
    const scope = event.scope || 'inapp';

    return `invalidate-${scope}-${name}`;
}

export function updatePageFromChangeInstruction(
    currentPage: Page,
    result: ChangeInstruction | PaginationResult,
    details: OnChangeDetails,
    afterPageUpdate?: (updatedPage: Page) => Page | null,
): Page | undefined {
    if (!result) return;

    let newPage: Page;

    let instructions = (
        isPaginationResult(result)
            ? isMultipleChangePaginationResult(result)
                ? result.pageChangeInstructions
                : [result.pageChangeInstruction]
            : [result]
    ).map((e) => e.instruction);

    // Find the current section when a section id is given
    const { section: currentSection, index: sectionIndex } =
        getSection(currentPage, details.sectionId) || {};

    // Copy the current page to newPage for updating the page when needed (only when not set yet)
    const setNewPage = () => {
        newPage = newPage || {
            ...currentPage,
            sections: [...(currentPage.sections || [])],
        };
    };

    const updateNewPage = () => {
        newPage.sections = newPage.sections?.map((section) => ({
            ...section,
            items: [...section.items],
        }));
    };

    for (let instruction of instructions) {
        switch (instruction.instruction) {
            // Page Changes
            case 'replacePage':
                newPage = instruction.newPage;
                updateNewPage();
                break;

            case 'addItemsToSection':
                const sectionToAddItemsTo = getSection(
                    currentPage,
                    instruction.newSectionToAppendItemsFrom.id,
                );
                if (!sectionToAddItemsTo) break;
                setNewPage();
                newPage.sections[sectionToAddItemsTo.index] = {
                    ...sectionToAddItemsTo.section,
                    items: [
                        ...sectionToAddItemsTo.section?.items,
                        ...instruction.newSectionToAppendItemsFrom.items,
                    ],
                };
                break;

            case 'removeItemsFromSections':
                const sectionsToRemoveFrom = (instruction as any)
                    .sectionAndItemIdsToRemove;
                if (!sectionsToRemoveFrom) break;
                setNewPage();

                sectionsToRemoveFrom.every((section) => {
                    const sectionToRemoveItems = getSection(
                        newPage,
                        section.id,
                    );
                    if (!sectionToRemoveItems) return;

                    const updatedItems =
                        sectionToRemoveItems.section.items.filter(
                            (item: any) => {
                                const itemToCheck = item?.item || item; // Due to Library Albums using item.item
                                const isItemToRemove = section.itemIds.some(
                                    (itemId) =>
                                        itemToCheck?.id?.includes(itemId),
                                );
                                return !isItemToRemove;
                            },
                        );

                    // Required for virtual scrolling views (Library Albums, Library Songs)
                    // Since items are stored in a sparse array and loaded on the fly, the virtual view
                    // needs the real total number in order to render the sparsely loaded rows correctly.
                    if (sectionToRemoveItems.section?.metadata?.total) {
                        // Total number of library items (e.g. songs, albums)
                        const oldTotal =
                            sectionToRemoveItems.section.metadata.total;
                        // Number of items removed from this section
                        const itemsRemoved =
                            sectionToRemoveItems.section.items.length -
                            updatedItems.length;
                        sectionToRemoveItems.section.metadata.total =
                            oldTotal - itemsRemoved;
                    }

                    newPage.sections[sectionToRemoveItems.index] = {
                        ...sectionToRemoveItems.section,
                        items: [...updatedItems],
                    };
                });

                break;

            case 'reorderItemsOnSection':
                const updateSectionData =
                    instruction.sectionAndItemIdsToReorder;
                if (
                    !updateSectionData?.id ||
                    !updateSectionData?.itemIds?.length
                )
                    break;

                setNewPage();
                const sectionToReorder = getSection(
                    newPage,
                    updateSectionData.id,
                );

                if (!sectionToReorder) break;

                const orderedItems = [];
                const originalItems = [...sectionToReorder.section.items];

                updateSectionData.itemIds.forEach((orderedItemId) => {
                    const itemIndex = originalItems.findIndex((item) =>
                        item.id.includes(orderedItemId),
                    );
                    if (itemIndex >= 0) {
                        // Add item to ordered list
                        orderedItems.push(originalItems[itemIndex]);
                        // Remove item from original list for:
                        // - duplicate items to get them in the right order
                        // - reducing the list and the amount of loops to find the next items
                        originalItems.splice(itemIndex, 1);
                    }
                });

                const updatedSection = {
                    ...sectionToReorder.section,
                    items: orderedItems,
                };

                newPage.sections[sectionToReorder.index] = updatedSection;

                break;

            // Page Changes & Section Changes
            // (updateItems instruction exists for both PageChangeInstruction and SectionChangeInstruction).
            // - SectionChangeInstruction has itemKind on the root ('itemKind' in instruction),
            // - PageChangeInstruction has it on each item wrapper { item, kind }.
            case 'updateItems':
                setNewPage();
                let items =
                    'itemKind' in instruction
                        ? instruction.items // Section
                        : instruction.items.map((item) => item.item); // Page

                newPage = getPageWithUpdatedItems(
                    newPage,
                    details.sectionId || null,
                    items,
                );
                break;

            case 'addSections':
                setNewPage();

                if (
                    instruction.sections.every(
                        (item) => item.itemKind === 'trackLockup',
                    ) &&
                    newPage.sections[newPage.sections.length - 1].itemKind ===
                        'trackListFooter'
                ) {
                    // Updating paginated playlist -- ensure existing footer stays at bottom
                    const footer = newPage.sections.pop();

                    newPage.sections.push(
                        ...(instruction.sections ?? []),
                        footer,
                    );
                } else {
                    newPage.sections.push(...(instruction.sections ?? []));
                }

                break;

            // Entire section changes
            case 'replaceSection':
                const sectionIndexToReplace =
                    getSection(currentPage, instruction.newSection.id)?.index ??
                    sectionIndex;
                if (
                    sectionIndexToReplace === undefined ||
                    sectionIndexToReplace === null ||
                    sectionIndexToReplace < 0
                )
                    break;
                setNewPage();
                newPage.sections[sectionIndexToReplace] = {
                    ...instruction.newSection,
                    items: [...instruction.newSection.items],
                };
                break;

            // Finer grained section update
            case 'updateSection':
                if (!newPage) {
                    setNewPage();
                }

                let sectionToUpdate = getSection(
                    newPage,
                    instruction.sectionID,
                );

                switch (sectionToUpdate.section.itemKind) {
                    case 'trackListFooter':
                        const footerItem = sectionToUpdate.section
                            .items[0] as ClassicalListItem;
                        const {
                            count: currentCount,
                            duration: currentDuration,
                        } = footerItem.meta;
                        const {
                            count: incomingCount,
                            duration: incomingDuration,
                        } = instruction.meta;

                        const meta = {
                            count: currentCount + incomingCount,
                            duration: currentDuration + incomingDuration,
                        };

                        const description = tracklistDescription(
                            meta.count,
                            meta.duration,
                            instruction.objectGraph,
                        );

                        newPage.sections[sectionToUpdate.index] = {
                            ...sectionToUpdate.section,
                            items: [
                                {
                                    ...footerItem,
                                    description,
                                    hidden: !!(result as PaginationResult)
                                        .nextIntent,
                                    meta,
                                },
                            ],
                        };
                        break;
                }

            case 'discardSection':
                if (!currentSection) break;
                setNewPage();
                newPage.sections.splice(sectionIndex, 1);
                break;

            case 'noOperation':
                // NO-OP
                break;

            default:
                // This catches a non-exhaustive match above. An error here means a case is missing!
                // See: https://stackoverflow.com/a/39419171
                const exhaustiveCheck: never = instruction;
                throw new Error(
                    `Unhandled Page Change instruction: ${exhaustiveCheck}`,
                );
        }
        if (newPage && isPaginationResult(result)) {
            newPage.nextIntent = result.nextIntent;
        }
        if (newPage && 'invalidationRulesChange' in result) {
            const invalidationChange = result.invalidationRulesChange;

            // `replace` has PageInvalidationRules[]
            if (invalidationChange.operation === 'replace') {
                newPage.invalidationRules = invalidationChange.newRules;
            }

            // `add` has SectionInvalidationRules[]
            // Add eventTriggers to current invalidationRules
            else if (invalidationChange.operation === 'add') {
                const sectionToUpdate = newPage.sections[sectionIndex];
                newPage.sections[sectionIndex] = {
                    ...sectionToUpdate,
                    invalidationRules: {
                        ...sectionToUpdate.invalidationRules,
                        eventTriggers: [
                            ...sectionToUpdate.invalidationRules.eventTriggers,
                            ...invalidationChange.newRules.eventTriggers,
                        ],
                    },
                };
            }
        }
        // Can be used to update the page after the intent got handled.
        // For example, to remove executed expiration invalidation events so they won't get triggered again on a new load.
        // It wouldn't make sense to update the page when it has been fully replaced.
        if (afterPageUpdate && instruction.instruction !== 'replacePage') {
            setNewPage();
            newPage = afterPageUpdate(newPage) || newPage;
        }
    }

    return newPage;
}

function removeInvalidationRulesFromPage(
    page: Page,
    { sectionId, trigger, event }: { sectionId?: string; trigger; event },
): Page | null {
    // Find the section to update when a section id is given
    const { section } = getSection(page, sectionId) || {};

    // Without using `any` getting errors later on when we want to update the source with a filtered list of it's own values
    const sourceToUpdate: any = sectionId ? section : page;
    const eventTriggers: any[] =
        sourceToUpdate?.invalidationRules?.eventTriggers || [];

    if (!sourceToUpdate || !eventTriggers?.length) return;

    // Find the invalidationRules that need to be removed
    const ruleIndex = eventTriggers.findIndex((eventTrigger) => {
        const eventTriggerWithoutEvents = { ...eventTrigger };
        delete eventTriggerWithoutEvents.events;

        // Need deep object equality
        return (
            JSON.stringify(eventTriggerWithoutEvents) ===
            JSON.stringify(trigger)
        );
    });

    if (ruleIndex < 0) return;

    const ruleToUpdate = eventTriggers[ruleIndex];

    const eventIndex = ruleToUpdate.events.findIndex((pageDataEvent) => {
        // Need deep object equality
        return JSON.stringify(pageDataEvent) === JSON.stringify(event);
    });

    if (eventIndex >= 0) {
        // Remove full rule when this was the only event
        if (ruleToUpdate.events.length === 1) {
            sourceToUpdate.invalidationRules.eventTriggers = [
                ...sourceToUpdate.invalidationRules.eventTriggers.filter(
                    (_trigger, index) => index !== ruleIndex,
                ),
            ];
        } else {
            ruleToUpdate.events = [
                ...ruleToUpdate.events.filter(
                    (_event, index) => index !== eventIndex,
                ),
            ];
        }

        return page;
    }
}

/**
 * Find the current section when a section id is given
 */
function getSection(
    page: Page,
    id?: string,
): { index: number; section: Section<unknown> } | null {
    let sectionIndex;
    const section =
        id &&
        page?.sections?.find((section, index) => {
            if (section.id === id) {
                sectionIndex = index;
                return true;
            }
        });

    if (section && ~sectionIndex) {
        return { section, index: sectionIndex };
    }
}

function isPaginationResult(
    result: ChangeInstruction | PaginationResult,
): result is PaginationResult {
    return (
        'pageChangeInstruction' in result || 'pageChangeInstructions' in result
    );
}

function getPageWithUpdatedItems(
    page: Page,
    sectionId: string | null,
    items: ComponentModel[] = [],
): Page {
    let newPage = { ...page };

    items.forEach((newItem) => {
        const { id } = newItem;
        if (!id) return;

        // The item id starts with the section id (sectionId - itemId).
        // we need to be cautious that will be safe for future usage.
        // Otherwise we have to loop over all items on each section to look for the item directly.
        const sectionIdToUpdate = sectionId || id.split(' - ')[0];

        // First find which section the item is in
        let sectionIndexToUpdate;
        const sectionToUpdate = page.sections.find((section, index) => {
            if (section.id === sectionIdToUpdate) {
                sectionIndexToUpdate = index;
                return true;
            }
        });
        // Find the index of the item we need to update
        const itemIndexToUpdate = sectionToUpdate?.items.findIndex(
            (item) => (item as Section<unknown>).id === id,
        );

        // Need to use spread assignments to have the objects updated and let Svelte render the updates
        if (sectionToUpdate && ~itemIndexToUpdate) {
            sectionToUpdate.items[itemIndexToUpdate] = { ...newItem };
            const updatedSection = {
                ...sectionToUpdate,
                items: [...sectionToUpdate.items],
            };

            newPage.sections[sectionIndexToUpdate] = updatedSection;
            newPage.sections = [...newPage.sections];
        }
    });
    return newPage;
}
